import type { CSSProperties, FC } from 'react';
import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableItemContext } from '../context/SortableItem.context';
import type { UniqueIdentifier } from '@dnd-kit/core';

type SortableItemProps = {
  id: UniqueIdentifier;
  children: React.ReactNode;
  hasDragHandle: boolean;
  customStyle?: React.CSSProperties;
  dragPlaceholderOpacity?: number;
};

export const SortableItem: FC<SortableItemProps> = props => {
  const { id, hasDragHandle, customStyle = {}, dragPlaceholderOpacity = 0.4, children } = props;
  const { attributes, listeners, setNodeRef, setDraggableNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: CSSProperties = {
    opacity: isDragging ? dragPlaceholderOpacity : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const context = useMemo(
    () => ({
      attributes,
      listeners,
      draggableNodeRef: setDraggableNodeRef,
    }),
    [attributes, listeners, setDraggableNodeRef],
  );

  return (
    <SortableItemContext.Provider value={context}>
      <div
        ref={setNodeRef}
        key={id}
        style={{ ...style, ...customStyle }}
        {...(!hasDragHandle ? attributes : {})}
        {...(!hasDragHandle ? listeners : {})}
      >
        {children}
      </div>
    </SortableItemContext.Provider>
  );
};
